import { useEffect, useRef, useState } from 'react';
import { Plus, Trash2, Save, X, ArrowUp, ArrowDown, Mic, Play, Pause, Loader2 } from 'lucide-react';
import { voicesAPI } from '../services/api.js';
import VoicePicker from './VoicePicker.jsx';
import { useAuth } from '../contexts/AuthContext.jsx';

// Vendor label shown on a persona row. Extend as providers are added.
const VENDOR_LABEL = { deepgram: 'Deepgram', kokoro: 'Kokoro', cartesia: 'Cartesia', elevenlabs: 'ElevenLabs' };
// Providers offered in the exec-admin persona picker. Deepgram is offered (414/530
// run Deepgram Aura voices); only the retired self-hosted Kokoro is hidden.
const EXEC_ADMIN_PROVIDERS = ['cartesia', 'elevenlabs', 'deepgram'];

// Per-persona "listen" button. Plays a short sample of THIS persona's voice using
// the same vendor-aware preview path the VoicePicker uses (voicesAPI.previewBlobUrl
// → GET /voices/preview). Self-contained: owns its own audio element + object URL,
// so each row plays/stops independently and a failure on one row never crashes the
// list. A persona with no provider/voiceId is shown disabled.
function PersonaPreviewButton({ provider, voiceId }) {
  const [playing, setPlaying] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const audioRef = useRef(null);
  const urlRef = useRef(null);

  const stop = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    if (urlRef.current) {
      URL.revokeObjectURL(urlRef.current);
      urlRef.current = null;
    }
    setPlaying(false);
  };

  // Stop + free the blob on unmount or when the persona's voice changes.
  useEffect(() => stop, [provider, voiceId]); // eslint-disable-line react-hooks/exhaustive-deps

  const previewable = Boolean(provider && voiceId);

  const toggle = async () => {
    if (playing) { stop(); return; }
    if (!previewable) return;
    setError('');
    setLoading(true);
    try {
      const url = await voicesAPI.previewBlobUrl(provider, voiceId);
      urlRef.current = url;
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => setPlaying(false);
      audio.onerror = () => { setError('Playback failed.'); setPlaying(false); };
      await audio.play();
      setPlaying(true);
    } catch (err) {
      setError(err.message || 'Preview unavailable.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      type="button"
      className="btn-ghost"
      onClick={toggle}
      disabled={!previewable || loading}
      title={error || (previewable ? 'Listen to a sample of this voice' : 'No voice to preview')}
      aria-label="Listen to this persona's voice"
    >
      {loading ? <Loader2 size={14} className="animate-spin" />
        : playing ? <Pause size={14} /> : <Play size={14} />}
    </button>
  );
}

// Personas editor — controls the panel-voice ROSTER for this tenant:
//   • how many AI voices answer (1, 2, or 3+),
//   • which ElevenLabs voice each uses (from a curated picker),
//   • each one's display name (the bracketed persona name the model speaks as,
//     e.g. "[Cowboy Jay] …"),
//   • the order (order-0 is the host: it voices the greeting).
//
// This is the ROSTER only. The panel ENGINE turns on automatically at 2+ rows,
// and what the personas SAY is the "Panel flow" (panelInstructions) field above.

const EMPTY = { voiceId: '', voiceProvider: '', displayName: '' };

export default function PersonasEditor() {
  const { tenant, selectedTenant, isOwner } = useAuth();
  const shownTenant = (isOwner && selectedTenant) || tenant;
  const isExecAdmin = !!shownTenant?.features?.executiveAdmin;
  const allowProviders = isExecAdmin ? EXEC_ADMIN_PROVIDERS : null;
  const [items, setItems] = useState([]);
  const [available, setAvailable] = useState([]);
  const [panelActive, setPanelActive] = useState(false);
  const [editing, setEditing] = useState({}); // id -> draft
  const [creating, setCreating] = useState(EMPTY);
  const [adding, setAdding] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmId, setConfirmId] = useState(null); // persona pending delete (in-app modal)

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const [r, a] = await Promise.all([
        voicesAPI.list(),
        voicesAPI.available().catch(() => ({ data: [] }))
      ]);
      const list = (r?.data || []).slice().sort((x, y) => x.order - y.order);
      setItems(list);
      setPanelActive(!!r?.panelActive);
      setAvailable(a?.data || []);
    } catch (err) {
      if (err.status !== 404) setError(err.message || 'Failed to load personas.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const voiceName = (voiceId) =>
    available.find((v) => v.voiceId === voiceId)?.name || voiceId;

  const startEdit = (item) =>
    setEditing((e) => ({ ...e, [item.id]: { voiceId: item.voiceId, voiceProvider: item.voiceProvider || 'elevenlabs', displayName: item.displayName } }));
  const cancelEdit = (id) =>
    setEditing((e) => { const n = { ...e }; delete n[id]; return n; });

  const saveEdit = async (id) => {
    const draft = editing[id];
    if (!draft || !draft.displayName.trim() || !draft.voiceId) return;
    setBusy(true);
    try {
      // Persist BOTH voiceProvider + voiceId so the agent routes Deepgram vs
      // ElevenLabs correctly for this persona.
      await voicesAPI.update(id, {
        voiceId: draft.voiceId,
        voiceProvider: draft.voiceProvider || 'elevenlabs',
        displayName: draft.displayName.trim()
      });
      cancelEdit(id);
      await load();
    } catch (err) {
      setError(err.message || 'Save failed.');
    } finally {
      setBusy(false);
    }
  };

  // Deletion is confirmed via an in-app modal (below) rather than the browser's
  // native confirm() — clicking the trash just opens it; doRemove does the work.
  const doRemove = async (id) => {
    setBusy(true);
    try {
      await voicesAPI.remove(id);
      setConfirmId(null);
      await load();
    } catch (err) {
      setError(err.message || 'Delete failed.');
      setConfirmId(null);
    } finally {
      setBusy(false);
    }
  };

  const create = async () => {
    if (!creating.displayName.trim() || !creating.voiceId) return;
    setBusy(true);
    try {
      await voicesAPI.create({
        voiceId: creating.voiceId,
        voiceProvider: creating.voiceProvider || 'elevenlabs',
        displayName: creating.displayName.trim()
      });
      setCreating(EMPTY);
      setAdding(false);
      await load();
    } catch (err) {
      setError(err.message || 'Create failed.');
    } finally {
      setBusy(false);
    }
  };

  // Move a persona up/down by swapping its order with its neighbor. We send the
  // neighbor's order so the backend re-normalizes; order-0 becomes the host.
  const move = async (index, dir) => {
    const target = index + dir;
    if (target < 0 || target >= items.length) return;
    const me = items[index];
    const other = items[target];
    setBusy(true);
    try {
      // Give "me" the other's order; normalize on the server settles ties by id.
      await voicesAPI.update(me.id, { order: other.order });
      await voicesAPI.update(other.id, { order: me.order });
      await load();
    } catch (err) {
      setError(err.message || 'Reorder failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl text-stone-100">Personas (AI voice roster)</h2>
          <p className="text-sm text-stone-400 mt-1">
            Control how many AI voices answer and who they are. Add one persona for a
            single-voice line, or two-plus to run the multi-voice panel. The first
            persona is the <span className="text-stone-300">host</span> — it voices the
            greeting.
          </p>
          <p className="text-xs text-stone-500 mt-2">
            This sets the <span className="text-stone-300">roster</span> (count, voice,
            and name). What each persona <span className="text-stone-300">says</span> —
            the script and how they talk together — lives in the
            {' '}<span className="text-stone-300">Panel flow</span> field above. The panel
            engine turns on automatically once you have 2 or more personas.
          </p>
        </div>
        {!adding && (
          <button type="button" className="btn-ghost" onClick={() => setAdding(true)} disabled={busy}>
            <Plus size={14} /> Add persona
          </button>
        )}
      </div>

      {error && (
        <p className="mb-4 rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-2 text-sm text-red-200">{error}</p>
      )}

      <div className="mb-4">
        <span
          className={
            'badge text-[10px] border ' +
            (panelActive
              ? 'bg-emerald-500/10 text-emerald-200 border-emerald-500/40'
              : 'bg-stone-800 text-stone-300 border-stone-700')
          }
        >
          {panelActive
            ? `Panel mode: ${items.length} voices`
            : items.length === 1
              ? 'Single voice'
              : 'No personas yet'}
        </span>
        {panelActive && (
          <span className="ml-2 text-xs text-stone-500">
            Also requires the engagement to be a panel engagement (see Active engagement above).
          </span>
        )}
      </div>

      {adding && (
        <div className="mb-4 rounded-xl border border-alfred-gold/30 p-4 space-y-3">
          {items.length === 1 && (
            <p className="rounded-lg border border-amber-800/50 bg-amber-950/30 px-3 py-2 text-xs text-amber-200">
              Adding a second voice turns this into a multi-voice <strong>panel</strong> (two AIs on the call).
              To just <em>change</em> the current voice instead, cancel and click <strong>Edit</strong> on the existing persona.
            </p>
          )}
          <div>
            <label className="field-label">Voice</label>
            <VoicePicker
              provider={creating.voiceProvider}
              voiceId={creating.voiceId}
              onChange={({ provider, voiceId }) => setCreating({ ...creating, voiceProvider: provider, voiceId })}
              allowProviders={allowProviders}
              autoFocus
            />
          </div>
          <div>
            <label className="field-label">Display name (persona)</label>
            <input
              className="field-input"
              placeholder="e.g. Cowboy Jay — the bracketed name the model speaks as"
              value={creating.displayName}
              onChange={(e) => setCreating({ ...creating, displayName: e.target.value })}
            />
            <p className="mt-1 text-xs text-stone-500">
              The model voices lines tagged <span className="text-stone-300">[{creating.displayName || 'Name'}]</span> in this voice. Match the names you use in the Panel flow.
            </p>
          </div>
          <div className="flex gap-2">
            <button type="button" className="btn-primary" onClick={create} disabled={busy || !creating.voiceId || !creating.displayName.trim()}>
              <Save size={14} /> Add
            </button>
            <button type="button" className="btn-ghost" onClick={() => { setAdding(false); setCreating(EMPTY); }}>
              <X size={14} /> Cancel
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <p className="text-sm text-stone-500">Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-stone-500">No personas yet. Add one to get started.</p>
      ) : (
        <ul className="divide-y divide-stone-900">
          {items.map((p, index) => {
            const draft = editing[p.id];
            if (draft) {
              return (
                <li key={p.id} className="py-4 space-y-3">
                  <div>
                    <label className="field-label">Voice</label>
                    <VoicePicker
                      provider={draft.voiceProvider}
                      voiceId={draft.voiceId}
                      onChange={({ provider, voiceId }) =>
                        setEditing((s) => ({ ...s, [p.id]: { ...s[p.id], voiceProvider: provider, voiceId } }))}
                      allowProviders={allowProviders}
                    />
                  </div>
                  <div>
                    <label className="field-label">Display name (persona)</label>
                    <input
                      className="field-input"
                      value={draft.displayName}
                      onChange={(e) => setEditing((s) => ({ ...s, [p.id]: { ...s[p.id], displayName: e.target.value } }))}
                    />
                  </div>
                  <div className="flex gap-2">
                    <button type="button" className="btn-primary" onClick={() => saveEdit(p.id)} disabled={busy}>
                      <Save size={14} /> Save
                    </button>
                    <button type="button" className="btn-ghost" onClick={() => cancelEdit(p.id)}>
                      <X size={14} /> Cancel
                    </button>
                  </div>
                </li>
              );
            }
            return (
              <li key={p.id} className="py-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Mic size={14} className="text-alfred-gold shrink-0" />
                      <span className="text-stone-100 font-medium">[{p.displayName}]</span>
                      {p.isDefault && (
                        <span className="badge bg-alfred-gold/15 text-alfred-gold border border-alfred-gold/40 text-[10px]">
                          Host
                        </span>
                      )}
                      <span className="badge bg-stone-800 text-stone-300 border border-stone-700 text-[10px]">
                        {voiceName(p.voiceId)}
                      </span>
                      <span className="badge bg-stone-900 text-stone-400 border border-stone-700 text-[10px]">
                        {VENDOR_LABEL[p.voiceProvider] || 'ElevenLabs'}
                      </span>
                    </div>
                    <p className="mt-1 text-xs text-stone-500">Order {p.order} · voice {p.voiceId}</p>
                  </div>
                  <div className="shrink-0 flex items-center gap-1">
                    <PersonaPreviewButton
                      provider={p.voiceProvider || 'elevenlabs'}
                      voiceId={p.voiceId}
                    />
                    <button
                      type="button"
                      className="btn-ghost"
                      onClick={() => move(index, -1)}
                      disabled={busy || index === 0}
                      aria-label="Move up"
                    >
                      <ArrowUp size={14} />
                    </button>
                    <button
                      type="button"
                      className="btn-ghost"
                      onClick={() => move(index, 1)}
                      disabled={busy || index === items.length - 1}
                      aria-label="Move down"
                    >
                      <ArrowDown size={14} />
                    </button>
                    <button type="button" className="btn-ghost" onClick={() => startEdit(p)} disabled={busy}>
                      Edit
                    </button>
                    <button
                      type="button"
                      className="btn-ghost text-red-300 hover:text-red-200"
                      onClick={() => setConfirmId(p.id)}
                      disabled={busy}
                      aria-label="Delete"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {confirmId && (() => {
        const target = items.find((p) => p.id === confirmId);
        const willDropBelowPanel = items.length === 2; // removing one leaves a single voice
        return (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4"
            onClick={() => !busy && setConfirmId(null)}
          >
            <div
              className="card w-full max-w-sm border border-stone-700 shadow-xl"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
            >
              <h3 className="text-stone-100 font-semibold">Remove this voice?</h3>
              <p className="mt-2 text-sm text-stone-300">
                {target ? <>Remove <span className="text-stone-100 font-medium">[{target.displayName}]</span> from the roster.</> : 'Remove this persona from the roster.'}
                {willDropBelowPanel && ' The panel engine turns off — the line drops to a single voice.'}
              </p>
              <div className="mt-4 flex justify-end gap-2">
                <button type="button" className="btn-ghost" onClick={() => setConfirmId(null)} disabled={busy}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn-primary bg-red-600 hover:bg-red-500 border-red-600"
                  onClick={() => doRemove(confirmId)}
                  disabled={busy}
                >
                  {busy ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />} Remove
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </section>
  );
}
